import { useMutation } from '@apollo/client';
import { css } from '@emotion/css';
import {
  usageTracker,
  UsageTrackerEvent,
  useAnalytics,
} from '@volumental/analytic';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FeetKeypoints, PaperFormat } from '../../../api/gql/graphql';
import { validateMutation } from '../../../api/mutations/validation-mutation';
import { Modal } from '../../../components/modal/modal';
import { useImageSrc } from '../../../services/asset-src-controller';
import { useScannerStore, useSessionStore } from '../../../store';
import { UserError } from '../../../types/types';
import { toUserError } from '../../../util/utils';
import { CompareCapture } from '../../result/components/compare-capture';
import {
  expectedKeyPoints,
  imageName,
  ScannerVariant,
  type View,
} from '../utils';
import { Camera } from './camera';
import { overlayVariant } from './camera/overlay/camera-overlay-types';
import { CaptureInstruction } from './capture-instruction';
import { ValidationModal } from './validation-modal';

const instructionContainer = css`
  height: 40%;
`;

const cameraContainer = css`
  position: relative;
  height: 60%;
  display: flex;
  flex-wrap: wrap;
`;

interface CaptureProps {
  view: View;
  onValid: (blob: Blob) => void;
  scannerVariant: ScannerVariant;
}

export function Capture({
  view,
  scannerVariant,
  onValid,
}: CaptureProps): JSX.Element {
  const { t } = useTranslation();
  const [validate, { data }] = useMutation(validateMutation);
  const { getAllExperiments } = useAnalytics();
  const { mode, session } = useSessionStore((store) => store);
  const { paperFormat, disableCaptureValidation } = useScannerStore(
    (store) => store,
  );

  const getImage = useImageSrc(scannerVariant);
  const [blob, setBlob] = useState<Blob>();
  const [error, setError] = useState<UserError>();
  const [showValidation, setShowValidation] = useState(false);
  const [validationStatus, setValidationStatus] = useState(false);

  const onImage = (capturedBlob: Blob): void => {
    usageTracker.track(UsageTrackerEvent.CaptureShot, {
      sizing_flow_type: scannerVariant,
      experience: mode,
    });
    setBlob(capturedBlob);
    if (disableCaptureValidation) {
      onValid(capturedBlob);
      return;
    }
    setShowValidation(true);
    validate({
      variables: {
        paperFormat: paperFormat ?? PaperFormat.A4,
        file: capturedBlob,
        session,
        subject: '',
        deviceID: usageTracker.trackerDeviceId(),
        experiments: getAllExperiments(),
        scannerType: scannerVariant,
        expectedKeyPoints: expectedKeyPoints(view),
      },
    })
      .then((res) => {
        if (res.data) {
          const validation = res.data.validate;
          if (validation.status) {
            setValidationStatus(true);
          } else {
            const code = validation.error?.code;
            const userError = code ? toUserError(code) : UserError.Unknown;
            usageTracker.track(UsageTrackerEvent.ValidationFailure, {
              user_error: userError,
              error_code: code,
              sizing_flow_type: scannerVariant,
              experience: mode,
            });
            setError(userError);
            setShowValidation(false);
          }
        }
      })
      .catch((error_: unknown) => {
        usageTracker.track(UsageTrackerEvent.ValidationFailure, {
          user_error: error_,
          sizing_flow_type: scannerVariant,
          experience: mode,
        });
        setError(UserError.Unknown);
        setShowValidation(false);
      });
  };

  return (
    <>
      <div className={instructionContainer}>
        <CaptureInstruction src={getImage(imageName(view))} />
      </div>
      <div className={cameraContainer}>
        <Camera
          onImage={onImage}
          overlay={overlayVariant(view, scannerVariant === ScannerVariant.Solo)}
        />
      </div>
      <Modal
        fitContent
        show={Boolean(showValidation && blob)}
        modalTitle={t('modalTitles.validationModal')}
      >
        {blob ? (
          <ValidationModal
            blob={blob}
            isValidated={validationStatus}
            onNext={() => {
              onValid(blob);
            }}
            paperCorners={data?.validate.paperCorners}
            feetKeypoints={data?.validate.feetKeypoints as FeetKeypoints}
          />
        ) : undefined}
      </Modal>

      <Modal
        show={error !== undefined}
        fitContent
        modalTitle={t('modalTitles.compareModal')}
      >
        <CompareCapture
          userError={error}
          onClick={() => {
            setError(undefined);
          }}
          buttonTitle={t('algoValidation.retake')}
          refImg={getImage(imageName(view))}
          capturedImg={blob}
        />
      </Modal>
    </>
  );
}
